/**
 * Sanitize JSX props produced by markdown-to-jsx for HTML tags the browser
 * doesn't recognize (e.g. LLM-emitted `<no>`, `<yes>`, `<sources>`,
 * `<llm-training>`). markdown-factory.js's createElement remaps these
 * unknown tags to `<span class="tag-X">` to suppress React warnings about
 * the unknown element type, but the original props ride along — including
 * boolean values produced by bare-attribute parsing (`<no title>` →
 * `{title: true}`).
 *
 * React warns when non-boolean HTML attributes receive a boolean (`title`,
 * `data-*`, `id`, etc.) and refuses to set the attribute. The pragmatic
 * cure: stringify boolean values so the renderer either writes the
 * stringified form (`title="true"`) or, where the attribute really doesn't
 * belong, simply doesn't trigger the warning.
 *
 * We deliberately don't try to be clever about which attributes are
 * "boolean HTML attributes" (like `hidden`, `disabled`) because the
 * remapped tag is now a `<span>` — none of those attributes are meaningful
 * on a span anyway. Stringifying any boolean value handles every case.
 *
 * Preserved unchanged: className, children, ref, key, style, event
 * handlers (onClick etc.) and any string/number prop.
 */
export const sanitizeUnknownTagProps = (props) => {
  if (!props || typeof props !== "object") return props;
  let mutated = null;
  for (const key of Object.keys(props)) {
    const v = props[key];
    if (typeof v === "boolean") {
      mutated = mutated || { ...props };
      mutated[key] = String(v); // "true" / "false"
    }
  }
  return mutated || props;
};
